import { MobileApp, PhoneFrame } from '../../components/mobile';
import { initialMobileTabFromLocation } from '../../components/mobile/initialTab';
import type { PlayerClub } from '../../lib/api/customerContext';
import { getPlayerTournamentHistory, calcPlayerPositionInTournament } from '../../engine';

export interface MobileAppViewsProps {
  view: string;
  /** May this session read the club's correspondence (#236)? Passed through to the app. */
  messagingEnabled?: boolean;
  effectivePlayerDB: any;
  bookings: any;
  plannedMatches: any;
  singleMatches: any;
  activities: any;
  archiveWithLive: any;
  /** The member's own server-computed history (#185). */
  memberHistory?: any[];
  memberMe?: any;
  bookingConfig: any;
  bookingZones: any;
  clubInfo: any;
  addBooking: any;
  /** The venue's equipment and its reservations (#911 slice 2). */
  assets?: any[];
  assetBookings?: any[];
  addAssetBooking?: any;
  persistActivities: any;
  applyServerActivity: any;
  recordResultForTournament?: any;
  recordKMResultForTournament?: any;
  addPlannedMatch?: any;
  updatePlannedMatchPlayers?: any;
  deletePlannedMatch?: any;
  completePlannedMatch?: any;
  goHome: () => void;
  /**
   * Render the app on its own, filling the viewport (the native Expo shell /
   * `?native=1`) instead of inside the desktop preview's phone frame.
   */
  standalone?: boolean;
  /** The club context the player has selected, or null for none. */
  activeClub?: PlayerClub | null;
  /** Change the active club context in place; null where it cannot change. */
  onSelectClub?: ((club: PlayerClub | null) => void) | null;
  /** Organizations this account may administer — the door to the portal. */
  organizations?: { tenantId: string; slug: string; name: string; kind: string }[];
  /** Enter one of them. */
  onEnterOrganization?: (tenantId: string) => void;
  /** Sign out — the app shows it only to a person the server named. */
  onSignOut?: (() => void) | null;
  /** How the club's own load is going (#801 review). */
  memberBootstrap?: {
    status: 'idle' | 'loading' | 'ready' | 'failed';
    active: boolean;
    retry: () => void;
  } | null;
}

// View-group for the member mobile app (`view === 'mobile'`). Inside the
// desktop shell it is previewed in a phone frame; the app itself is
// frame-agnostic, so a real mobile build mounts <MobileApp> directly.
export function MobileAppViews({
  view,
  messagingEnabled,
  effectivePlayerDB,
  bookings,
  plannedMatches,
  singleMatches,
  activities,
  archiveWithLive,
  memberHistory,
  memberMe,
  bookingConfig,
  bookingZones,
  clubInfo,
  addBooking,
  assets,
  assetBookings,
  addAssetBooking,
  persistActivities,
  applyServerActivity,
  recordResultForTournament,
  recordKMResultForTournament,
  addPlannedMatch,
  updatePlannedMatchPlayers,
  deletePlannedMatch,
  completePlannedMatch,
  goHome,
  standalone = false,
  activeClub = null,
  onSelectClub = null,
  organizations = [],
  onEnterOrganization,
  onSignOut = null,
  memberBootstrap = null,
}: MobileAppViewsProps) {
  if (view !== 'mobile') return null;
  const app = (
    <MobileApp
      // A link may name the tab to open on — the pay-link mail opens
      // «Reservasjoner» (#1137). Read at mount only, as `initialTab` is.
      initialTab={initialMobileTabFromLocation()}
      messagingEnabled={messagingEnabled}
      activeClub={activeClub}
      onSelectClub={onSelectClub}
      organizations={organizations}
      onEnterOrganization={onEnterOrganization}
      onSignOut={onSignOut}
      memberBootstrap={memberBootstrap}
      effectivePlayerDB={effectivePlayerDB || []}
      bookings={bookings || []}
      plannedMatches={plannedMatches || []}
      singleMatches={singleMatches || []}
      activities={activities || []}
      archiveWithLive={archiveWithLive || []}
      memberHistory={memberHistory}
      memberMe={memberMe}
      bookingConfig={bookingConfig}
      bookingZones={bookingZones || []}
      clubInfo={clubInfo}
      addBooking={addBooking}
      assets={assets || []}
      assetBookings={assetBookings || []}
      addAssetBooking={addAssetBooking}
      persistActivities={persistActivities}
      applyServerActivity={applyServerActivity}
      getPlayerTournamentHistory={getPlayerTournamentHistory}
      calcPlayerPositionInTournament={calcPlayerPositionInTournament}
      recordResultForTournament={recordResultForTournament}
      recordKMResultForTournament={recordKMResultForTournament}
      addPlannedMatch={addPlannedMatch}
      updatePlannedMatchPlayers={updatePlannedMatchPlayers}
      deletePlannedMatch={deletePlannedMatch}
      completePlannedMatch={completePlannedMatch}
    />
  );
  if (standalone) {
    // Full viewport, pinned to the screen (100dvh follows the mobile browser
    // chrome). The shell's safe areas are handled natively, outside the page.
    // Shorter by the «new version» banner while it shows, so the tab bar sits
    // above it rather than under it (`NEW_BUILD_BANNER_HEIGHT_VAR`).
    return (
      <div
        data-testid="mobile-standalone"
        className="fixed inset-0 flex flex-col"
        style={{
          height: 'calc(100dvh - var(--new-build-banner-h, 0px))',
          background: '#f7f8f5',
        }}
      >
        {app}
      </div>
    );
  }
  return (
    <PhoneFrame label="Forhåndsvisning · mobilapp" onExit={goHome}>
      {app}
    </PhoneFrame>
  );
}
